/**
 * QA-04 (Mario, 13 Oct: a reload during "Assistant is replying" lost the conversation) — the chat's stream across a
 * reload, kept as DATA: an element's tag, a short list of its attributes, its text. Drawn back node by node with
 * `createElement`, `setAttribute` and text nodes — never markup (there is no `innerHTML` anywhere in `src/`), never an id
 * (the page counts its ids from zero again), never a style (the CSP refuses a style attribute, and a style here is only
 * ever a gesture caught mid-way), never a form field, a script, a frame or a picture.
 *
 * Every payee name, reference, transcript and bank or model sentence in the stream is third-party text: it was a text node
 * and comes back as one. A record read back from storage is checked against the same lists as the page it came from, so a
 * record that was tampered with draws nothing those lists do not allow.
 *
 * The past comes back as the past: nothing in it rises again, and every control in it is spent — a chip group as a spent
 * group (its own style), any other button disabled. What is live again (a card to sign, an open question, Ask again) is
 * drawn by the chat itself, from the bank.
 */
export type SnapNode = string | { readonly t: string; readonly a?: Readonly<Record<string, string>>; readonly c?: readonly SnapNode[] };

const SVG = "http://www.w3.org/2000/svg";

/** The HTML the chat draws (lib/dom.ts `el` callers), and nothing else. */
const HTML_TAGS: ReadonlySet<string> = new Set([
  "div", "span", "p", "b", "i", "em", "strong", "small", "h1", "h2", "h3", "h4", "ul", "ol", "li", "dl", "dt", "dd",
  "table", "thead", "tbody", "tr", "th", "td", "code", "pre", "bdi", "br", "wbr", "details", "summary", "button", "a",
  "time", "abbr", "sup", "sub", "s", "del", "ins", "mark", "label", "section", "header", "footer", "figure", "figcaption",
]);
/** An icon is the sprite's (lib/icon.ts): an <svg> holding one <use href="#i-…">. */
const SVG_TAGS: ReadonlySet<string> = new Set(["svg", "use"]);

const KEPT: ReadonlySet<string> = new Set(["class", "role", "hidden", "lang", "dir", "title", "colspan", "scope", "datetime", "open"]);
const SVG_KEPT: ReadonlySet<string> = new Set(["class", "viewBox", "width", "height", "focusable", "aria-hidden"]);
/** Ids are not kept, so neither is anything that names one. */
const ARIA_REFS: ReadonlySet<string> = new Set([
  "aria-describedby", "aria-labelledby", "aria-controls", "aria-owns", "aria-activedescendant", "aria-details", "aria-errormessage", "aria-flowto",
]);
const ARIA = /^aria-[a-z]{1,24}$/;
const DATA = /^data-[a-z0-9-]{1,40}$/;
/** The paused card's "Call 1799 · ScamShield" stays a call; no other link is kept (DCTA-221 review: that number only). */
const TEL = /^tel:1799$/;
/**
 * DCTA-221 review: the past says nothing again — a live region drawn back (an error `role=alert`, a status) would be
 * announced when the chat is mounted. Live roles and live attributes are not kept.
 */
const LIVE_ROLES: ReadonlySet<string> = new Set(["alert", "status", "log", "marquee", "timer", "alertdialog"]);
const LIVE_ARIA: ReadonlySet<string> = new Set(["aria-live", "aria-atomic", "aria-relevant"]);
const SPRITE = /^#i-[a-z0-9-]{1,32}$/;

const TEXT_MAX = 4000;
const VALUE_MAX = 2000;
const DEPTH_MAX = 40;
/** Elements kept by default — the newest part of a long conversation (a card is ~100). */
const SNAP_BUDGET = 4000;
const REBUILD_BUDGET = 6000;

function keptAttribute(tag: string, name: string, value: string): boolean {
  if (value.length > VALUE_MAX) return false;
  if (tag === "svg") return SVG_KEPT.has(name);
  if (tag === "use") return name === "href" && SPRITE.test(value);
  if (name === "href") return tag === "a" && TEL.test(value);
  if (name === "role" && LIVE_ROLES.has(value.trim().toLowerCase())) return false;
  if (LIVE_ARIA.has(name)) return false;
  if (KEPT.has(name)) return true;
  // A press caught mid-way (lib/press.ts) and a card caught busy are not part of what was shown.
  if (name === "data-pressed" || name === "aria-busy") return false;
  return DATA.test(name) || (ARIA.test(name) && !ARIA_REFS.has(name));
}

/** The entrance played once; the past never rises again. */
const withoutEnter = (cls: string): string =>
  cls
    .split(/\s+/)
    .filter((c) => c !== "" && c !== "enter")
    .join(" ");

/** Never kept: the greeting (drawn again), the words being heard, the wait, the typing dots, the stream's end. */
function transient(e: Element): boolean {
  return e.matches(".greet, .stream-end, .interim, .turn.work") || (e.matches(".turn") && e.querySelector(".typing[role='status']") !== null);
}

function tagOf(e: Element): string | undefined {
  const tag = e.localName;
  if (e.namespaceURI === SVG) return SVG_TAGS.has(tag) ? tag : undefined;
  return HTML_TAGS.has(tag) ? tag : undefined;
}

function snap(n: Node, budget: { left: number }, depth: number): SnapNode | undefined {
  if (n.nodeType === 3) {
    const t = n.textContent ?? "";
    return t === "" ? undefined : t.slice(0, TEXT_MAX);
  }
  if (!(n instanceof Element) || depth > DEPTH_MAX) return undefined;
  const tag = tagOf(n);
  // A card's number on screen is never kept — only its mask (ui/readcard.ts `CardDetails`).
  if (tag === undefined || n.matches(".secret:not(.masked)")) return undefined;
  budget.left -= 1;
  const a: Record<string, string> = {};
  for (const attr of n.attributes) {
    if (!keptAttribute(tag, attr.name, attr.value)) continue;
    const value = attr.name === "class" ? withoutEnter(attr.value) : attr.value;
    if (attr.name !== "class" || value !== "") a[attr.name] = value;
  }
  const c: SnapNode[] = [];
  for (const child of n.childNodes) {
    const s = snap(child, budget, depth + 1);
    if (s !== undefined) c.push(s);
  }
  return { t: tag, ...(Object.keys(a).length === 0 ? {} : { a }), ...(c.length === 0 ? {} : { c }) };
}

/**
 * The stream's past, oldest first: every top-level node but the transient ones, up to (not including) `before` — a part the
 * chat draws again itself (an open question's turn and what follows it). A long conversation keeps its newest part:
 * `budget` elements, counted from the newest node back.
 */
export function snapStream(body: Element, o: { readonly before?: Element | undefined; readonly budget?: number } = {}): SnapNode[] {
  const tops = [...body.children];
  const cut = o.before === undefined ? -1 : tops.findIndex((t) => t === o.before || t.contains(o.before ?? null));
  const end = cut === -1 ? tops.length : cut;
  const budget = { left: o.budget ?? SNAP_BUDGET };
  const out: SnapNode[] = [];
  for (let i = end - 1; i >= 0; i -= 1) {
    const top = tops[i];
    if (top === undefined || transient(top)) continue;
    const s = snap(top, budget, 0);
    // This node did not fit: the older ones go with it.
    if (budget.left < 0) break;
    if (s !== undefined) out.push(s);
  }
  return out.reverse();
}

function rebuild(s: unknown, budget: { left: number }, depth: number): Node | undefined {
  if (typeof s === "string") return document.createTextNode(s.slice(0, TEXT_MAX));
  if (s === null || typeof s !== "object" || depth > DEPTH_MAX || budget.left <= 0) return undefined;
  const { t, a, c } = s as { readonly t?: unknown; readonly a?: unknown; readonly c?: unknown };
  if (typeof t !== "string") return undefined;
  const svg = SVG_TAGS.has(t);
  if (!svg && !HTML_TAGS.has(t)) return undefined;
  budget.left -= 1;
  const e = svg ? document.createElementNS(SVG, t) : document.createElement(t);
  if (a !== null && typeof a === "object") {
    for (const [name, value] of Object.entries(a as Record<string, unknown>)) {
      if (typeof value !== "string" || !keptAttribute(t, name, value)) continue;
      e.setAttribute(name, name === "class" ? withoutEnter(value) : value);
    }
  }
  if (Array.isArray(c)) {
    for (const child of c as unknown[]) {
      const n = rebuild(child, budget, depth + 1);
      if (n !== undefined) e.append(n);
    }
  }
  return e;
}

/** Every control in the past is spent: a chip group as a spent group (app.css 103), any other button disabled. */
function spend(root: DocumentFragment): void {
  for (const group of root.querySelectorAll<HTMLElement>(".opts")) {
    group.dataset["spent"] = "true";
    for (const chip of group.querySelectorAll("button")) chip.setAttribute("aria-disabled", "true");
  }
  for (const button of root.querySelectorAll("button")) {
    button.type = "button";
    if (button.closest(".opts") === null) button.disabled = true;
  }
}

/** The past, drawn back from data (`snapStream`'s, or anything read back from storage), every control in it spent. */
export function rebuildStream(nodes: unknown): DocumentFragment {
  const out = document.createDocumentFragment();
  if (!Array.isArray(nodes)) return out;
  const budget = { left: REBUILD_BUDGET };
  for (const s of nodes as unknown[]) {
    const n = rebuild(s, budget, 0);
    if (n !== undefined) out.append(n);
  }
  spend(out);
  return out;
}
